import { useEffect, useState } from "react";
import { api, fmtEUR } from "@/lib/api";
import { itemColors } from "@/lib/category-colors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
    Plus,
    PencilSimple,
    Trash,
    Package,
    MagnifyingGlass,
    Pause,
    Play,
    Keyboard,
    Camera,
} from "@phosphor-icons/react";
import BarcodeScanner from "@/components/BarcodeScanner";
import { useNumpad } from "@/lib/numpad-context";
import { useSortableTable, SortIndicator } from "@/lib/use-sortable";
import { useAuth } from "@/lib/auth-context";

const VAT_CHOICES = [0, 6, 13, 24];
const CATEGORY_CHOICES = ["ΨΙΛΙΚΑ", "ΔΙΑΦΟΡΑ", "ΑΝΑΨΥΚΤΙΚΑ", "ΦΑΓΗΤΟ", "ΚΑΦΕΣ"];
const POSITIONS = Array.from({ length: 21 }, (_, i) => i);

const empty = {
    code: "",
    description: "",
    barcode1: "",
    barcode2: "",
    barcode3: "",
    unit_price: "",
    vat_rate: "13",
    category: "ΔΙΑΦΟΡΑ",
    screen_position: "0",
    paused: false,
    free_dispense: false,
};

export default function Products() {
    const { canEdit } = useAuth();
    const numpad = useNumpad();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL"); // ALL | ACTIVE | PAUSED
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [scannerOpen, setScannerOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/items");
            setItems(data);
        } catch {
            toast.error("Αποτυχία φόρτωσης ειδών");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const openNew = async () => {
        setEditing(null);
        setForm({ ...empty, code: "..." });
        setDialogOpen(true);
        try {
            const { data } = await api.get("/items/next-code");
            setForm((f) => ({ ...f, code: data.code }));
        } catch {
            setForm((f) => ({ ...f, code: "" }));
            toast.error("Αποτυχία ανάκτησης επόμενου κωδικού");
        }
    };

    const openEdit = (it) => {
        setEditing(it);
        setForm({
            code: it.code,
            description: it.description,
            barcode1: it.barcode1 || "",
            barcode2: it.barcode2 || "",
            barcode3: it.barcode3 || "",
            unit_price: String(it.unit_price ?? ""),
            vat_rate: String(it.vat_rate ?? 13),
            category: it.category || "ΔΙΑΦΟΡΑ",
            screen_position: String(it.screen_position ?? 0),
            paused: !!it.paused,
            free_dispense: !!it.free_dispense,
        });
        setDialogOpen(true);
    };

    const handleSave = async (e) => {
        e?.preventDefault?.();
        if (!form.description.trim()) {
            toast.error("Η περιγραφή είναι υποχρεωτική");
            return;
        }
        if (form.description.trim().length > 30) {
            toast.error("Η περιγραφή δεν μπορεί να ξεπερνά τους 30 χαρακτήρες");
            return;
        }
        // Free dispense: αν είναι ενεργό, τιμή πάντα 0 (αγνόησε το input)
        let price;
        if (form.free_dispense) {
            price = 0;
        } else {
            price = parseFloat(String(form.unit_price).replace(",", "."));
            if (isNaN(price) || price < 0) {
                toast.error("Μη έγκυρη τιμή μονάδας");
                return;
            }
            if (price === 0) {
                toast.error(
                    "Η τιμή 0,00€ επιτρέπεται μόνο όταν είναι ενεργή η ΔΩΡΕΑΝ ΔΙΑΘΕΣΗ"
                );
                return;
            }
        }
        setSaving(true);
        try {
            const payload = {
                description: form.description.trim(),
                barcode1: form.barcode1.trim(),
                barcode2: form.barcode2.trim(),
                barcode3: form.barcode3.trim(),
                unit_price: price,
                vat_rate: parseInt(form.vat_rate, 10),
                category: form.category,
                screen_position: parseInt(form.screen_position, 10),
                paused: !!form.paused,
                free_dispense: !!form.free_dispense,
            };
            if (editing) {
                await api.put(`/items/${editing.id}`, payload);
                toast.success("Το είδος ενημερώθηκε");
            } else {
                await api.post("/items", payload);
                toast.success("Το είδος προστέθηκε");
            }
            setDialogOpen(false);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Σφάλμα αποθήκευσης", {
                duration: 7000,
            });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        try {
            await api.delete(`/items/${confirmDelete.id}`);
            toast.success("Το είδος διαγράφηκε");
            setConfirmDelete(null);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Αποτυχία διαγραφής", {
                duration: 6000,
            });
            setConfirmDelete(null);
        }
    };

    const togglePause = async (it) => {
        const next = !it.paused;
        try {
            await api.post(`/items/${it.id}/pause`, { paused: next });
            toast.success(
                next ? `Σε παύση: ${it.description}` : `Ενεργό: ${it.description}`
            );
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Αποτυχία ενέργειας");
        }
    };

    const filtered = items.filter((it) => {
        if (statusFilter === "ACTIVE" && it.paused) return false;
        if (statusFilter === "PAUSED" && !it.paused) return false;
        if (!filter.trim()) return true;
        const q = filter.toLowerCase();
        return (
            it.code.toLowerCase().includes(q) ||
            it.description.toLowerCase().includes(q) ||
            (it.barcode1 || "").includes(q) ||
            (it.barcode2 || "").includes(q) ||
            (it.barcode3 || "").includes(q) ||
            (it.category || "").toLowerCase().includes(q)
        );
    });

    const counts = {
        ALL: items.length,
        ACTIVE: items.filter((i) => !i.paused).length,
        PAUSED: items.filter((i) => i.paused).length,
    };

    const itemsSort = useSortableTable(filtered, "code", "asc", {
        code: (i) => i.code,
        description: (i) => i.description || "",
        unit_price: (i) => Number(i.unit_price ?? 0),
        vat_rate: (i) => Number(i.vat_rate ?? 0),
        category: (i) => i.category || "",
        barcodes: (i) => [i.barcode1, i.barcode2, i.barcode3].filter(Boolean).join(" "),
        screen_position: (i) => Number(i.screen_position ?? 0),
    });
    const sortedItems = itemsSort.sorted;

    return (
        <div className="flex-1 min-h-0 overflow-y-auto p-6 md:p-10 max-w-7xl w-full" data-testid="products-page">
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4 fade-up">
                <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5F66] mb-2">
                        ΔΙΑΧΕΙΡΙΣΗ
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">
                        ΠΡΟΙΟΝΤΑ
                    </h1>
                    <p className="mt-2 text-sm text-[#5C5F66]">
                        Σύνολο: {items.length} είδη
                    </p>
                </div>
                <Button
                    onClick={openNew}
                    data-testid="add-item-button"
                    className={`rounded-none h-12 px-6 bg-[#002FA7] hover:bg-[#001D66] text-white font-bold tracking-wide transition-all duration-200 active:scale-[0.98] ${
                        canEdit ? "" : "hidden"
                    }`}
                >
                    <Plus size={18} weight="bold" className="mr-2" />
                    ΝΕΟ ΕΙΔΟΣ
                </Button>
            </div>

            {/* Filter */}
            <div className="mb-6 flex items-center gap-3 flex-wrap fade-up">
                <div className="relative flex-1 max-w-md min-w-[260px]">
                    <MagnifyingGlass
                        size={18}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5F66]"
                    />
                    <Input
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        placeholder="Αναζήτηση κατά κωδικό, περιγραφή, barcode..."
                        data-testid="item-filter-input"
                        className="rounded-none h-11 pl-10 pr-2 border-[#E2E4E9] focus-visible:border-[#002FA7] focus-visible:ring-1 focus-visible:ring-[#002FA7]"
                    />
                </div>
                <button
                    onClick={() =>
                        numpad.toggle({
                            value: filter,
                            label: "Αναζήτηση είδους →",
                            onChange: setFilter,
                            onApply: (v) => setFilter(v),
                        })
                    }
                    data-testid="products-numpad-btn"
                    className="h-11 px-4 bg-white border-2 border-[#5856D6] text-[#5856D6] hover:bg-[#5856D6] hover:text-white font-bold text-xs uppercase tracking-wider flex items-center gap-2 transition-colors"
                    title="Αριθμητικό πληκτρολόγιο"
                >
                    <Keyboard size={18} weight="bold" />
                    NUMPAD
                </button>
                <button
                    onClick={() => setScannerOpen(true)}
                    data-testid="products-scanner-btn"
                    className="h-11 px-4 bg-white border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white font-bold text-xs uppercase tracking-wider flex items-center gap-2 transition-colors"
                    title="Σάρωση barcode"
                >
                    <Camera size={18} weight="bold" />
                    ΣΑΡΩΣΗ
                </button>
                <div
                    className="flex items-stretch border-2 border-[#0A0A0A]"
                    data-testid="status-filter"
                >
                    {[
                        { key: "ALL", label: "ΟΛΑ" },
                        { key: "ACTIVE", label: "ΕΝΕΡΓΑ" },
                        { key: "PAUSED", label: "ΣΕ ΠΑΥΣΗ" },
                    ].map((opt) => {
                        const active = statusFilter === opt.key;
                        return (
                            <button
                                key={opt.key}
                                onClick={() => setStatusFilter(opt.key)}
                                data-testid={`status-filter-${opt.key.toLowerCase()}`}
                                className={`px-4 h-11 text-xs font-bold uppercase tracking-[0.15em] border-r-2 last:border-r-0 border-[#0A0A0A] transition-colors ${
                                    active
                                        ? opt.key === "PAUSED"
                                            ? "bg-[#FFCC00] text-[#0A0A0A]"
                                            : "bg-[#0A0A0A] text-white"
                                        : "bg-white text-[#5C5F66] hover:bg-[#F4F5F7]"
                                }`}
                            >
                                {opt.label}
                                <span
                                    className={`ml-2 font-mono text-[10px] ${
                                        active ? "opacity-90" : "text-[#5C5F66]"
                                    }`}
                                >
                                    {counts[opt.key]}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="border border-[#E2E4E9] bg-white fade-up">
                {loading ? (
                    <div className="p-16 text-center text-sm font-bold uppercase tracking-[0.2em] text-[#5C5F66]">
                        Φορτωση...
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="p-16 text-center">
                        <Package
                            size={48}
                            weight="thin"
                            className="mx-auto text-[#5C5F66] mb-4"
                        />
                        <div className="text-sm text-[#5C5F66]">
                            {items.length === 0
                                ? "Δεν υπάρχουν είδη ακόμη. Πατήστε «ΝΕΟ ΕΙΔΟΣ»."
                                : "Δεν βρέθηκαν είδη με αυτά τα κριτήρια."}
                        </div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-b border-[#0A0A0A] hover:bg-transparent">
                                    <SortableItemHead sort={itemsSort} sortKey="code" label="Κωδικος" />
                                    <SortableItemHead sort={itemsSort} sortKey="description" label="Περιγραφη" />
                                    <SortableItemHead sort={itemsSort} sortKey="unit_price" label="Τιμη" align="right" />
                                    <SortableItemHead sort={itemsSort} sortKey="vat_rate" label="ΦΠΑ" align="right" />
                                    <SortableItemHead sort={itemsSort} sortKey="category" label="Κατηγορια" />
                                    <SortableItemHead sort={itemsSort} sortKey="barcodes" label="Barcodes" />
                                    <SortableItemHead sort={itemsSort} sortKey="screen_position" label="Θεση" align="right" />
                                    <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#0A0A0A] h-12 text-right w-32">
                                        Ενεργειες
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sortedItems.map((it) => {
                                    const barcodes = [it.barcode1, it.barcode2, it.barcode3]
                                        .filter(Boolean);
                                    const paused = !!it.paused;
                                    const free = !!it.free_dispense;
                                    const colors = itemColors(it);
                                    const rowStyle = paused
                                        ? undefined
                                        : { backgroundColor: colors.bg };
                                    return (
                                        <TableRow
                                            key={it.id}
                                            style={rowStyle}
                                            className={`border-b border-[#E2E4E9] ${
                                                paused
                                                    ? "bg-[#FFF8DD] hover:bg-[#FFEFAE] opacity-90"
                                                    : "hover:brightness-95"
                                            }`}
                                            data-testid={`item-row-${it.code}`}
                                        >
                                            <TableCell>
                                                <Badge className="rounded-none bg-[#0A0A0A] text-white font-mono text-xs">
                                                    {it.code}
                                                </Badge>
                                            </TableCell>
                                            <TableCell
                                                className={`font-semibold max-w-[240px] truncate ${
                                                    paused
                                                        ? "text-[#7A5C00] line-through"
                                                        : "text-[#0A0A0A]"
                                                }`}
                                            >
                                                {it.description}
                                                {paused && (
                                                    <span className="ml-2 inline-block px-1.5 py-0.5 bg-[#FFCC00] text-[#0A0A0A] text-[9px] font-black uppercase tracking-wider align-middle no-underline">
                                                        ΣΕ ΠΑΥΣΗ
                                                    </span>
                                                )}
                                                {it.free_dispense && (
                                                    <span
                                                        className="ml-2 inline-block px-1.5 py-0.5 bg-[#34C759] text-white text-[9px] font-black uppercase tracking-wider align-middle no-underline"
                                                        data-testid={`free-dispense-badge-${it.code}`}
                                                        title="ΔΩΡΕΑΝ ΔΙΑΘΕΣΗ — Επιτρέπεται τιμή 0€"
                                                    >
                                                        🎁 ΔΩΡΕΑΝ
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell className={`text-right font-bold font-mono text-sm ${paused ? "text-[#7A5C00]" : "text-[#0A0A0A]"}`}>
                                                {fmtEUR(it.unit_price)}
                                            </TableCell>
                                            <TableCell className="text-right font-mono text-xs text-[#5C5F66]">
                                                {it.vat_rate}%
                                            </TableCell>
                                            <TableCell className="text-xs">
                                                <span className="inline-block px-2 py-0.5 bg-[#F4F5F7] border border-[#E2E4E9] font-bold tracking-wide">
                                                    {it.category}
                                                </span>
                                            </TableCell>
                                            <TableCell className="font-mono text-[11px] text-[#5C5F66] max-w-[200px] truncate">
                                                {barcodes.length > 0
                                                    ? barcodes.join(" / ")
                                                    : "—"}
                                            </TableCell>
                                            <TableCell className="text-right font-mono text-xs text-[#5C5F66]">
                                                {it.screen_position}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    <button
                                                        onClick={() => togglePause(it)}
                                                        data-testid={`toggle-pause-${it.code}`}
                                                        className={`p-2 transition-colors ${
                                                            paused
                                                                ? "text-[#1F8A3E] hover:bg-[#1F8A3E] hover:text-white"
                                                                : "text-[#FF9500] hover:bg-[#FF9500] hover:text-white"
                                                        }`}
                                                        title={paused ? "Επανενεργοποίηση" : "Παύση"}
                                                    >
                                                        {paused ? (
                                                            <Play size={16} weight="bold" />
                                                        ) : (
                                                            <Pause size={16} weight="bold" />
                                                        )}
                                                    </button>
                                                    {canEdit && (
                                                        <>
                                                            <button
                                                                onClick={() => openEdit(it)}
                                                                data-testid={`edit-item-${it.code}`}
                                                                className="p-2 hover:bg-[#0A0A0A] hover:text-white text-[#0A0A0A] transition-colors"
                                                                title="Επεξεργασία"
                                                            >
                                                                <PencilSimple size={16} weight="bold" />
                                                            </button>
                                                            <button
                                                                onClick={() => setConfirmDelete(it)}
                                                                data-testid={`delete-item-${it.code}`}
                                                                className="p-2 hover:bg-[#FF3B30] hover:text-white text-[#FF3B30] transition-colors"
                                                                title="Διαγραφή"
                                                            >
                                                                <Trash size={16} weight="bold" />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>

            {/* Add/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent
                    className="rounded-none border-2 border-[#0A0A0A] max-w-3xl p-0 gap-0 max-h-[92vh] overflow-y-auto"
                    data-testid="item-form-dialog"
                >
                    <DialogHeader className="p-7 border-b border-[#E2E4E9]">
                        <DialogTitle className="font-heading text-3xl font-black tracking-tighter text-[#0A0A0A]">
                            {editing ? "ΕΠΕΞΕΡΓΑΣΙΑ ΕΙΔΟΥΣ" : "ΝΕΟ ΕΙΔΟΣ"}
                        </DialogTitle>
                        <DialogDescription className="text-base text-[#5C5F66]">
                            {editing
                                ? "Ο κωδικός είδους δεν αλλάζει."
                                : "Ο κωδικός θα δοθεί αυτόματα από το σύστημα."}
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSave}>
                        <div className="p-7 space-y-5">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                <div className="space-y-2">
                                    <Label className="text-sm font-bold uppercase tracking-[0.15em]">
                                        Κωδικος ειδους
                                    </Label>
                                    <Input
                                        value={form.code}
                                        readOnly
                                        disabled
                                        data-testid="form-item-code"
                                        className="rounded-none h-14 text-2xl font-bold border-[#E2E4E9] bg-[#F4F5F7] text-[#0A0A0A] font-mono cursor-not-allowed"
                                    />
                                </div>
                                <div className="space-y-2 md:col-span-2">
                                    <Label className="text-sm font-bold uppercase tracking-[0.15em]">
                                        Περιγραφη * (εως 30 χαρ.)
                                    </Label>
                                    <Input
                                        value={form.description}
                                        maxLength={30}
                                        onChange={(e) =>
                                            setForm({ ...form, description: e.target.value })
                                        }
                                        placeholder="π.χ. Καφές φίλτρου"
                                        data-testid="form-item-description"
                                        className="rounded-none h-14 text-base border-[#E2E4E9] focus-visible:border-[#002FA7] focus-visible:ring-1 focus-visible:ring-[#002FA7]"
                                    />
                                    <div className="text-xs text-[#5C5F66] text-right font-mono">
                                        {form.description.length}/30
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                <div className="space-y-2">
                                    <Label className="text-sm font-bold uppercase tracking-[0.15em]">
                                        Barcode 1
                                    </Label>
                                    <Input
                                        value={form.barcode1}
                                        onChange={(e) =>
                                            setForm({ ...form, barcode1: e.target.value })
                                        }
                                        placeholder="EAN-13 / Code-128..."
                                        data-testid="form-item-barcode1"
                                        className="rounded-none h-14 text-base border-[#E2E4E9] focus-visible:border-[#002FA7] focus-visible:ring-1 focus-visible:ring-[#002FA7] font-mono"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-sm font-bold uppercase tracking-[0.15em]">
                                        Barcode 2
                                    </Label>
                                    <Input
                                        value={form.barcode2}
                                        onChange={(e) =>
                                            setForm({ ...form, barcode2: e.target.value })
                                        }
                                        data-testid="form-item-barcode2"
                                        className="rounded-none h-14 text-base border-[#E2E4E9] focus-visible:border-[#002FA7] focus-visible:ring-1 focus-visible:ring-[#002FA7] font-mono"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-sm font-bold uppercase tracking-[0.15em]">
                                        Barcode 3
                                    </Label>
                                    <Input
                                        value={form.barcode3}
                                        onChange={(e) =>
                                            setForm({ ...form, barcode3: e.target.value })
                                        }
                                        data-testid="form-item-barcode3"
                                        className="rounded-none h-14 text-base border-[#E2E4E9] focus-visible:border-[#002FA7] focus-visible:ring-1 focus-visible:ring-[#002FA7] font-mono"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                                <div className="space-y-2">
                                    <Label className="text-sm font-bold uppercase tracking-[0.15em]">
                                        {form.free_dispense
                                            ? "Τιμη μοναδας (ΔΩΡΕΑΝ)"
                                            : "Τιμη μοναδας *"}
                                    </Label>
                                    <Input
                                        type="text"
                                        inputMode="decimal"
                                        value={
                                            form.free_dispense
                                                ? "0,00"
                                                : form.unit_price
                                        }
                                        onChange={(e) =>
                                            setForm({ ...form, unit_price: e.target.value })
                                        }
                                        placeholder="0.00"
                                        disabled={!!form.free_dispense}
                                        data-testid="form-item-price"
                                        className={`rounded-none h-14 text-xl font-bold border-[#E2E4E9] focus-visible:border-[#002FA7] focus-visible:ring-1 focus-visible:ring-[#002FA7] text-right font-mono ${
                                            form.free_dispense
                                                ? "bg-[#EAF7EE] text-[#1F8A3E] cursor-not-allowed"
                                                : ""
                                        }`}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-sm font-bold uppercase tracking-[0.15em]">
                                        ΦΠΑ
                                    </Label>
                                    <Select
                                        value={form.vat_rate}
                                        onValueChange={(v) => setForm({ ...form, vat_rate: v })}
                                    >
                                        <SelectTrigger
                                            data-testid="form-item-vat"
                                            className="rounded-none h-14 text-base border-[#E2E4E9] focus:ring-1 focus:ring-[#002FA7]"
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-none">
                                            {VAT_CHOICES.map((v) => (
                                                <SelectItem
                                                    key={v}
                                                    value={String(v)}
                                                    className="rounded-none text-base h-12"
                                                >
                                                    {v}%
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-sm font-bold uppercase tracking-[0.15em]">
                                        Κατηγορια
                                    </Label>
                                    <Select
                                        value={form.category}
                                        onValueChange={(v) => setForm({ ...form, category: v })}
                                    >
                                        <SelectTrigger
                                            data-testid="form-item-category"
                                            className="rounded-none h-14 text-base border-[#E2E4E9] focus:ring-1 focus:ring-[#002FA7]"
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-none">
                                            {CATEGORY_CHOICES.map((c) => (
                                                <SelectItem
                                                    key={c}
                                                    value={c}
                                                    className="rounded-none text-base h-12"
                                                >
                                                    {c}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-sm font-bold uppercase tracking-[0.15em]">
                                        Θεση οθονης
                                    </Label>
                                    <Select
                                        value={form.screen_position}
                                        onValueChange={(v) =>
                                            setForm({ ...form, screen_position: v })
                                        }
                                    >
                                        <SelectTrigger
                                            data-testid="form-item-position"
                                            className="rounded-none h-14 text-base border-[#E2E4E9] focus:ring-1 focus:ring-[#002FA7]"
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-none max-h-[300px]">
                                            {POSITIONS.map((p) => (
                                                <SelectItem
                                                    key={p}
                                                    value={String(p)}
                                                    className="rounded-none text-base h-12"
                                                >
                                                    {p}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Pause toggle */}
                            <div
                                className={`border-2 p-4 flex items-center justify-between gap-4 ${
                                    form.paused
                                        ? "border-[#FFCC00] bg-[#FFF8DD]"
                                        : "border-[#E2E4E9]"
                                }`}
                                data-testid="form-item-pause-row"
                            >
                                <div className="flex items-center gap-3">
                                    {form.paused ? (
                                        <Pause
                                            size={22}
                                            weight="fill"
                                            className="text-[#7A5C00]"
                                        />
                                    ) : (
                                        <Play
                                            size={22}
                                            weight="fill"
                                            className="text-[#1F8A3E]"
                                        />
                                    )}
                                    <div>
                                        <div className="text-sm font-bold uppercase tracking-[0.15em]">
                                            ΠΑΥΣΗ ΕΙΔΟΥΣ
                                        </div>
                                        <div className="text-xs text-[#5C5F66] mt-0.5">
                                            {form.paused
                                                ? "Το είδος ΔΕΝ θα εμφανίζεται προς πώληση στο POS."
                                                : "Το είδος είναι ενεργό και διαθέσιμο προς πώληση."}
                                        </div>
                                    </div>
                                </div>
                                <Switch
                                    checked={!!form.paused}
                                    onCheckedChange={(v) =>
                                        setForm({ ...form, paused: !!v })
                                    }
                                    data-testid="form-item-pause"
                                />
                            </div>

                            {/* Free dispense toggle */}
                            <div
                                className={`border-2 p-4 flex items-center justify-between gap-4 ${
                                    form.free_dispense
                                        ? "border-[#34C759] bg-[#EAF7EE]"
                                        : "border-[#E2E4E9]"
                                }`}
                                data-testid="form-item-free-dispense-row"
                            >
                                <div className="flex items-center gap-3">
                                    <span className="text-2xl" aria-hidden>
                                        🎁
                                    </span>
                                    <div>
                                        <div className="text-sm font-bold uppercase tracking-[0.15em]">
                                            ΔΩΡΕΑΝ ΔΙΑΘΕΣΗ
                                        </div>
                                        <div className="text-xs text-[#5C5F66] mt-0.5 leading-relaxed">
                                            {form.free_dispense
                                                ? "Επιτρέπεται τιμή 0,00€ — το είδος δίνεται δωρεάν."
                                                : "Όταν είναι ενεργό, το είδος μπορεί να έχει τιμή 0,00€."}
                                        </div>
                                    </div>
                                </div>
                                <Switch
                                    checked={!!form.free_dispense}
                                    onCheckedChange={(v) =>
                                        setForm((f) => ({
                                            ...f,
                                            free_dispense: !!v,
                                            // Auto-set price to 0 when turning ON;
                                            // clear the field when turning OFF so the
                                            // user explicitly enters a price.
                                            unit_price: v ? "0" : "",
                                        }))
                                    }
                                    data-testid="form-item-free-dispense"
                                />
                            </div>
                        </div>
                        <DialogFooter className="p-7 border-t border-[#E2E4E9] gap-3">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setDialogOpen(false)}
                                className="rounded-none h-14 px-8 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#F4F5F7] font-bold text-base"
                            >
                                ΑΚΥΡΟ
                            </Button>
                            <Button
                                type="submit"
                                disabled={saving}
                                data-testid="form-item-save"
                                className="rounded-none h-14 px-8 bg-[#002FA7] hover:bg-[#001D66] text-white font-bold text-base transition-all duration-200 active:scale-[0.98]"
                            >
                                {saving
                                    ? "ΑΠΟΘΗΚΕΥΣΗ..."
                                    : editing
                                      ? "ΕΝΗΜΕΡΩΣΗ"
                                      : "ΔΗΜΙΟΥΡΓΙΑ"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <AlertDialog
                open={!!confirmDelete}
                onOpenChange={(o) => !o && setConfirmDelete(null)}
            >
                <AlertDialogContent
                    className="rounded-none border-2 border-[#0A0A0A]"
                    data-testid="delete-item-dialog"
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-heading font-black tracking-tighter">
                            ΔΙΑΓΡΑΦΗ ΕΙΔΟΥΣ
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Πρόκειται να διαγράψετε το είδος{" "}
                            <strong>{confirmDelete?.description}</strong> (
                            {confirmDelete?.code}). Η ενέργεια δεν αναιρείται.
                            <br />
                            <span className="text-xs text-[#7A5C00]">
                                Σημ.: Είδη με υπάρχουσες πωλήσεις δεν διαγράφονται. Χρησιμοποιήστε ΠΑΥΣΗ.
                            </span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none border-[#0A0A0A]">
                            ΑΚΥΡΟ
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            data-testid="confirm-delete-item"
                            className="rounded-none bg-[#FF3B30] hover:bg-[#cc2f26] text-white font-bold"
                        >
                            ΔΙΑΓΡΑΦΗ
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <BarcodeScanner
                open={scannerOpen}
                onClose={() => setScannerOpen(false)}
                onScan={(text) => {
                    setFilter((text || "").trim());
                    setScannerOpen(false);
                }}
            />
        </div>
    );
}

function SortableItemHead({ sort, sortKey, label, align = "left" }) {
    const active = sort.sortKey === sortKey;
    return (
        <TableHead
            onClick={() => sort.toggle(sortKey)}
            data-testid={`sort-items-${sortKey}`}
            className={`text-[10px] font-bold uppercase tracking-[0.15em] text-[#0A0A0A] h-12 cursor-pointer select-none hover:bg-[#F4F5F7] ${
                align === "right" ? "text-right" : ""
            } ${active ? "bg-[#F4F5F7]" : ""}`}
        >
            {label}
            <SortIndicator active={active} direction={sort.sortDir} />
        </TableHead>
    );
}

